import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { ChevronLeft, ChevronRight, Info, Star, Calendar as CalendarIcon, Clock, ChevronDown } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isToday } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import MiniCalendar from '../components/MiniCalendar';
import BookingInfoModal from '../components/BookingInfoModal';
import DaySummaryModal from '../components/DaySummaryModal';
import CreateBookingModal from '../components/CreateBookingModal';
import ResourceDetailModal from '../components/ResourceDetailModal';
import CustomScrollbar from '../components/CustomScrollbar';
import DayColumn from '../components/calendar/DayColumn';
import TimeColumn from '../components/calendar/TimeColumn';
import ResourceColumn from '../components/calendar/ResourceColumn';
import ResourceHeader from '../components/calendar/ResourceHeader';
import DayHeader from '../components/calendar/DayHeader';
import '../components/MobileListView.css';
import { useModalPersistence } from '../utils/modalPersistence';
import { CalendarBooking, getCalendarBookings, getBasicBookingBlocks, getBooking } from '../lib/bookings';
import { getBookingFeedback } from '../lib/feedback';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { usePermissionCheck } from '../hooks/usePermissionCheck';

interface BookingPayload {
  id: string;
  business_id: string;
  [key: string]: any;
}

// Cache for basic blocks
const basicBlocksCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for full details
const detailsCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache duration in milliseconds (15 minutes)
const CACHE_DURATION = 15 * 60 * 1000;

export default function CalendarV2() {
  const { selectedBusiness } = useBusiness();
  const { canAccess, currentResource } = usePermissionCheck();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarTitle, setCalendarTitle] = useState('');
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [viewStartDate, setViewStartDate] = useState<Date>(new Date());
  const [viewEndDate, setViewEndDate] = useState<Date>(new Date());
  const [loadedBookingIds, setLoadedBookingIds] = useState<Set<string>>(new Set());
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingInfoModalOpen, setIsBookingInfoModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDaySummaryOpen, setIsDaySummaryOpen] = useState(false);
  const [daySummaryPosition, setDaySummaryPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isCalculatingSales, setIsCalculatingSales] = useState(true);
  const [salesInitiallyLoaded, setSalesInitiallyLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Reference to the calendar instance
  const calendarRef = useRef<FullCalendar>(null);
  const isMounted = useRef(true);
  
  // Add reference to track fetch calls to prevent multiple simultaneous fetches
  const isFetching = useRef(false);
  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasInitialScrolled = useRef(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);

  // Add feedback state
  const [bookingFeedback, setBookingFeedback] = useState<Map<string, { rating: number; hasText: boolean }>>(new Map());

  // Modal persistence hook
  const { getModalState, clearModalState } = useModalPersistence();

  // Restore modal state on component mount
  useEffect(() => {
    const restoreModalState = async () => {
      const savedState = getModalState();
      if (savedState && savedState.isOpen && savedState.bookingId) {
        try {
          console.log('📱 Restoring modal state from localStorage:', savedState);
          
          // Fetch the fresh booking data
          const booking = await getBooking(savedState.bookingId);
          if (booking) {
            setSelectedBooking(booking);
            setIsBookingInfoModalOpen(true);
            console.log('📱 Modal state restored successfully');
          } else {
            console.warn('📱 Booking not found, clearing saved state');
            clearModalState();
          }
        } catch (error) {
          console.error('📱 Error restoring modal state:', error);
          clearModalState();
        }
      }
    };

    // Only restore on initial mount
    restoreModalState();
  }, []); // Empty dependency array for mount-only effect

  // Get cache key based on business ID and date range
  const getCacheKey = useCallback((businessId: string, startDate: Date, endDate: Date) => {
    return `${businessId}_${startDate.toISOString()}_${endDate.toISOString()}`;
  }, []);

  // Memoize the currentMonthStart and currentMonthEnd
  const dateRange = useMemo(() => {
    const currentMonthStart = new Date(viewStartDate);
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);
    
    const currentMonthEnd = new Date(currentMonthStart);
    currentMonthEnd.setMonth(currentMonthEnd.getMonth() + 1);
    currentMonthEnd.setDate(0);
    currentMonthEnd.setHours(23, 59, 59, 999);
    
    return { currentMonthStart, currentMonthEnd };
  }, [viewStartDate]);
  
  // Optimized fetch with immediate rendering - no more two-phase loading
  const fetchBookings = useCallback(async (forceFetch = false) => {
    if (!selectedBusiness?.id) return;
    if (isFetching.current) return;
    
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    try {
      setIsLoading(true);
      setError(null);
      isFetching.current = true;
      
      const { currentMonthStart, currentMonthEnd } = dateRange;
      
      // Determine if we should filter by resource based on the viewAllBookings permission
      const shouldFilterByResource = !canAccess.viewAllBookings && currentResource?.id;
      const filterResourceId = shouldFilterByResource ? currentResource.id : undefined;
      
      // Include resource filtering in cache key if filtering is active
      const cacheKey = getCacheKey(
        selectedBusiness.id, 
        currentMonthStart, 
        currentMonthEnd
      ) + (filterResourceId ? `_resource_${filterResourceId}` : '');
      
      // Check cache first
      const cachedBookings = basicBlocksCache.get(cacheKey);
      const now = Date.now();
      
      let bookings: CalendarBooking[];
      if (!forceFetch && cachedBookings && (now - cachedBookings.timestamp) < CACHE_DURATION) {
        bookings = cachedBookings.data;
      } else {
        // Use getCalendarBookings instead of getBasicBookingBlocks to support resource filtering
        bookings = await getCalendarBookings(
          selectedBusiness.id,
          currentMonthStart,
          currentMonthEnd,
          true, // isLocationBooking = true for CalendarV2
          filterResourceId // Pass the resource ID for filtering if needed
        );
        basicBlocksCache.set(cacheKey, { data: bookings, timestamp: now });
        
        console.log(`Fetched ${bookings.length} location bookings${shouldFilterByResource ? ` filtered for resource ${currentResource.first_name} ${currentResource.last_name}` : ' (all bookings)'}`);
      }

      // Update the calendar immediately with complete visual data
      if (isMounted.current) {
        setBookings(bookings);
        setLoadedBookingIds(new Set(bookings.map(b => b.id)));
        
        // Load feedback for completed bookings
        loadFeedbackForBookings(bookings);
        
        setTimeout(() => {
          if (isMounted.current) {
            setIsLoading(false);
          }
        }, 200); // Small delay to ensure everything is fully rendered
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (isMounted.current) {
        setError('Failed to load bookings. Please try refreshing the page.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
      isFetching.current = false;
    }
  }, [selectedBusiness?.id, dateRange, getCacheKey, canAccess.viewAllBookings, currentResource?.id]);
  
  // Calculate total sales for the current month view
  const calculateTotalSales = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setIsCalculatingSales(true);
    
    try {
      // Get the exact month and year being displayed in the calendar view
      const calendarYear = currentDate.getFullYear();
      const calendarMonth = currentDate.getMonth(); // 0-based month (0 = January)
      
      // Calculate first and last day of the displayed month
      const monthStart = new Date(calendarYear, calendarMonth, 1);
      const monthEnd = new Date(calendarYear, calendarMonth + 1, 0);
      
      // Format to YYYY-MM-DD
      const formattedMonthStart = monthStart.toISOString().split('T')[0];
      const formattedMonthEnd = monthEnd.toISOString().split('T')[0];
      
      console.log('Calculating sales for month:', {
        currentMonth: `${monthStart.toLocaleString('default', { month: 'long' })} ${calendarYear}`,
        dateRange: `${formattedMonthStart} to ${formattedMonthEnd}`
      });
      
      // Fetch bookings that END within this exact month range, excluding mobile bookings
      const { data: salesData, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_booking', false) // Only include location bookings (non-mobile)
        .gte('end_date', formattedMonthStart)
        .lte('end_date', formattedMonthEnd);
      
      if (salesError) {
        console.error('Error fetching sales data:', salesError);
        return;
      }
      
      if (salesData && salesData.length > 0) {
        // Log bookings included in the calculation 
        console.log('Location bookings included in current month sales calculation:', 
          salesData.map(b => ({ 
            id: b.id,
            booking_number: b.booking_number,
            end_date: b.end_date, 
            subtotal: b.subtotal,
            tax_amount: b.tax_amount,
            total_amount: b.total_amount,
            mobile_booking: b.mobile_booking,
            sales_amount: b.subtotal || (b.total_amount - (b.tax_amount || 0))
          }))
        );
        
        // Sum up all sales amounts (excluding tax)
        const sum = salesData.reduce((acc, booking) => {
          // If subtotal is available, use it directly
          if (booking.subtotal !== null && booking.subtotal !== undefined) {
            return acc + booking.subtotal;
          }
          // Otherwise calculate by subtracting tax from total
          const taxAmount = booking.tax_amount || 0;
          const totalAmount = booking.total_amount || 0;
          return acc + (totalAmount - taxAmount);
        }, 0);
        
        if (isMounted.current) {
          setTotalSales(sum);
          setSalesInitiallyLoaded(true);
        }
      } else {
        console.log(`No location bookings with end dates in ${monthStart.toLocaleString('default', { month: 'long' })} ${calendarYear}`);
        if (isMounted.current) {
          setTotalSales(0);
          setSalesInitiallyLoaded(true);
        }
      }
    } catch (err) {
      console.error('Error calculating sales:', err);
      if (isMounted.current) {
        setSalesInitiallyLoaded(true);
      }
    } finally {
      if (isMounted.current) {
        setIsCalculatingSales(false);
      }
    }
  }, [selectedBusiness?.id, currentDate]);
  
  // Defer sales calculation until after calendar loads to improve initial performance
  useEffect(() => {
    if (selectedBusiness?.id && calendarRef.current && bookings.length > 0) {
      // Defer sales calculation by 300ms to not block calendar rendering
      setTimeout(() => {
        calculateTotalSales();
      }, 300);
    }
  }, [calculateTotalSales, selectedBusiness?.id, currentDate, bookings.length]);

  // Initialize on component mount
  useEffect(() => {
    isMounted.current = true;
    
    // Immediately fetch bookings on mount if business is selected
    if (selectedBusiness?.id) {
      fetchBookings();
    }
    
    return () => {
      isMounted.current = false;
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [fetchBookings, selectedBusiness?.id]);
  
  // Update the calendar title whenever the current date changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      setCalendarTitle(calendarApi.view.title);
    }
  }, [currentDate]);
  
  // Set up Supabase real-time subscription for bookings
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;
    
    console.log('Setting up real-time subscription for bookings');
    
    // Subscribe to changes in the bookings table
    const subscription = supabase
      .channel('bookings-changes')
      .on('postgres_changes', 
        { 
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: 'bookings',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        async (payload: any) => {
          const bookingId = payload.new?.id || payload.old?.id;
          console.log('Received booking change:', {
            event: payload.eventType,
            bookingId,
            timestamp: new Date().toISOString()
          });

          // Handle different types of changes
          switch (payload.eventType) {
            case 'INSERT':
              console.log('New booking created:', payload.new);
              // Fetch the new booking details
              await fetchBookings(true);
              break;
              
            case 'UPDATE':
              console.log('Booking updated:', {
                old: payload.old,
                new: payload.new
              });
              // Refresh the bookings to get updated data
              await fetchBookings(true);
              break;
              
            case 'DELETE':
              console.log('Booking deleted:', payload.old);
              // Remove the deleted booking from the state
              if (payload.old?.id) {
                setBookings(prevBookings => 
                  prevBookings.filter(booking => booking.id !== payload.old.id)
                );
              }
              break;
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });
    
    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up real-time subscription');
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Listen for immediate booking creation events from CreateBookingModal
  useEffect(() => {
    const handleBookingCreated = (event: any) => {
      const { bookingId, businessId } = event.detail;
      console.log('📅 Received immediate bookingCreated event:', { bookingId, businessId });
      
      // Only refresh if the booking is for the current business
      if (businessId === selectedBusiness?.id) {
        console.log('📅 Refreshing calendar immediately for new booking');
        fetchBookings(true); // Force fetch to get the new booking
      }
    };

    // Add event listener for immediate updates
    window.addEventListener('bookingCreated', handleBookingCreated);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('bookingCreated', handleBookingCreated);
    };
  }, [selectedBusiness?.id, fetchBookings]);
  
  // Handle date changes (when calendar navigates)
  const handleDatesSet = useCallback((arg: any) => {
    setCurrentDate(arg.view.currentStart);
    setCalendarTitle(arg.view.title);
    setViewStartDate(arg.start);
    setViewEndDate(arg.end);
  }, []);
  
  // Optimized navigation handlers with faster transitions
  const handlePrev = useCallback(() => {
    if (calendarRef.current) {
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  const handleNext = useCallback(() => {
    if (calendarRef.current) {
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  const handleToday = useCallback(() => {
    if (calendarRef.current) {
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.today();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  // Handle event click
  const handleEventClick = useCallback(async (info: any) => {
    try {
      setIsLoading(true);
      setSelectedBooking(null); // Clear any previous booking
      // Fetch full booking details first
      const booking = await getBooking(info.event.id);
      setSelectedBooking(booking);
      setIsBookingInfoModalOpen(true); // Open modal only after booking is loaded
    } catch (err) {
      setError('Failed to load booking details. Please try again.');
      setIsBookingInfoModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Memoize the event content renderer to show loading state
  const renderEventContent = useCallback((eventInfo: any) => {
    const isLoaded = loadedBookingIds.has(eventInfo.event.id);
    // Prefer drop_off_time, fallback to start_time
    const dropOffTime = eventInfo.event.extendedProps.drop_off_time;
    const startTime = eventInfo.event.extendedProps.start_time;
    const timeToShow = dropOffTime || startTime;
    const bookingNumber = eventInfo.event.extendedProps.booking_number;
    // Get secondary color from both possible locations
    const secondaryColor = eventInfo.event.extendedProps.secondary_color || 
                          eventInfo.event.extendedProps.secondaryColor || 
                          eventInfo.event.secondaryColor;
    
    // Get booking status to determine if we should show secondary color
    const bookingStatus = eventInfo.event.extendedProps.status;
    const shouldShowSecondaryColor = secondaryColor && 
                                   bookingStatus !== 'in_progress' && 
                                   bookingStatus !== 'completed' && 
                                   bookingStatus !== 'cancelled';
    
    // Get feedback data for this booking
    const feedbackData = bookingFeedback.get(eventInfo.event.id);
    const showFeedbackRating = bookingStatus === 'completed' && feedbackData;
    
    // Calculate payment status
    const totalAmount = eventInfo.event.extendedProps.total_amount || 0;
    const payments = eventInfo.event.extendedProps.payments || [];
    const totalPaid = payments
      .filter((p: any) => p.status === 'completed')
      .reduce((sum: number, p: any) => sum + p.amount, 0);

    let paymentStatus = '';
    let paymentStampColor = '';
    
    if (totalAmount > 0) {
      if (totalPaid === 0) {
        paymentStatus = 'UNPAID';
        paymentStampColor = 'text-white';
      } else if (totalPaid >= totalAmount) {
        paymentStatus = 'PAID';
        paymentStampColor = 'text-white';
      } else {
        paymentStatus = 'PARTIAL';
        paymentStampColor = 'text-white';
      }
    }
    
    let formattedTime = '';
    if (timeToShow) {
      // Format as HH:MM AM/PM
      const [h, m] = timeToShow.split(':');
      const date = new Date();
      date.setHours(Number(h), Number(m), 0, 0);
      formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // Extract only the model from the title (assuming format like "2022 Honda Civic" or "Honda Civic")
    let modelOnly = 'Untitled';
    if (eventInfo.event.title) {
      const titleParts = eventInfo.event.title.split(' ');
      // If we have at least 2 parts, assume the last part is the model
      if (titleParts.length >= 2) {
        modelOnly = titleParts[titleParts.length - 1];
      } else {
        modelOnly = eventInfo.event.title;
      }
    }
    
    return (
      <div className="p-1 relative overflow-hidden">
        {shouldShowSecondaryColor && (
          <div 
            className="secondary-color-line absolute left-0 right-0" 
            style={{ 
              backgroundColor: secondaryColor,
              height: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              marginLeft: '-5px',
              marginRight: '-5px',
              boxShadow: '0 0 4px rgba(0,0,0,0.2)',
              borderRadius: '2px'
            }}
          />
        )}
        
        {/* Feedback Rating for completed bookings */}
        {showFeedbackRating && (
          <div 
            className="absolute top-0 right-0 z-30 flex items-center px-1 py-0.5 rounded-bl-md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              fontSize: isMobile ? '8px' : '10px'
            }}
          >
            <Star 
              size={isMobile ? 10 : 12} 
              className="text-yellow-400 fill-current mr-0.5" 
            />
            <span className="text-gray-800 font-medium">
              {feedbackData.rating.toFixed(1)}
            </span>
          </div>
        )}
        
        {/* Payment Status Stamp */}
        {paymentStatus && (
          <div 
            className={`absolute bottom-0 right-0 z-20 px-0.5 py-0 text-[5px] sm:text-[8px] font-bold rounded-tl-sm ${paymentStampColor}`}
            style={{ 
              fontSize: isMobile ? '5px' : '8px',
              lineHeight: '1',
              letterSpacing: '0.02em',
              padding: isMobile ? '1px 2px' : '2px 4px'
            }}
          >
            {paymentStatus}
          </div>
        )}
        
        {/* Display booking number and customer name on first line */}
        <div className="flex items-center space-x-1 relative z-10">
          {bookingNumber && (
            <div className="text-[9px] sm:text-xs font-medium">{bookingNumber}</div>
          )}
          {isLoaded && eventInfo.event.extendedProps.customer && (
            <div className="text-[9px] sm:text-xs font-medium">- {eventInfo.event.extendedProps.customer}</div>
          )}
        </div>
        
        {/* Display only vehicle model on second line */}
        <div className="text-[9px] sm:text-sm truncate relative z-10 mt-0.5">{modelOnly}</div>
        
        {/* Display time on third line */}
        <div className="text-[8px] sm:text-xs opacity-75 truncate relative z-10 mt-0.5">{formattedTime}</div>
        
        {!isLoaded && (
          <div className="text-[8px] sm:text-xs opacity-50 relative z-10">Loading...</div>
        )}
      </div>
    );
  }, [loadedBookingIds, isMobile, bookingFeedback]);
  
  // Force a calendar redraw/rerender when bookings change
  useEffect(() => {
    if (calendarRef.current && bookings.length > 0) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  }, [bookings, bookingFeedback]);
  
  // Force a redraw when the loading state changes to ensure colors are applied
  useEffect(() => {
    if (!isLoading && calendarRef.current && bookings.length > 0) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.updateSize();
    }
  }, [isLoading, bookings]);
  
  // Memoize calendar events to prevent unnecessary re-renders
  const calendarEvents = useMemo(() => bookings, [bookings]);
  
  // Add a dayCellContent renderer for the info button
  const renderDayCellContent = (arg: any) => {
  return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <button
          type="button"
          tabIndex={0}
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#f3f4f6',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 4,
            cursor: 'pointer',
            padding: 0
          }}
          aria-label="Info"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setSelectedDay(arg.date);
            const rect = e.currentTarget.getBoundingClientRect();
            setDaySummaryPosition({
              x: rect.left,
              y: rect.top,
              width: rect.width,
              height: rect.height
            });
            setIsDaySummaryOpen(true);
          }}
        >
          <Info size={12} color="#6b7280" />
        </button>
        <span className={arg.isToday ? 'calendar-today-pill' : ''}>{arg.dayNumberText}</span>
      </div>
    );
  };
  
  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fix for mobile calendar height issue
  useEffect(() => {
    if (!isMobile || !calendarRef.current) return;
    
    // Function to fix calendar height on mobile
    const fixCalendarHeight = () => {
      try {
        // Target the key elements that might be causing height restrictions
        const viewHarness = document.querySelector('.fc-view-harness');
        const daygridBody = document.querySelector('.fc-daygrid-body');
        const scrollGrid = document.querySelector('.fc-scrollgrid');
        const scrollers = document.querySelectorAll('.fc-scroller');
        const container = document.querySelector('.calendar-v2-container');
        
        // Remove any height restrictions
        if (viewHarness) {
          (viewHarness as HTMLElement).style.height = 'auto';
          (viewHarness as HTMLElement).style.minHeight = '1200px'; // Much taller
        }
        
        if (daygridBody) {
          (daygridBody as HTMLElement).style.height = 'auto';
        }
        
        if (scrollGrid) {
          (scrollGrid as HTMLElement).style.height = 'auto';
        }
        
        // Fix all scrollers
        scrollers.forEach(scroller => {
          (scroller as HTMLElement).style.height = 'auto';
          (scroller as HTMLElement).style.overflow = 'visible';
        });
        
        // Add extra padding to container
        if (container) {
          (container as HTMLElement).style.paddingBottom = '800px';
        }
        
        // Force a redraw of the calendar if calendar reference exists
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.updateSize();
        }
      } catch (error) {
        console.error('Error fixing calendar height:', error);
      }
    };
    
    // Apply fix after calendar is rendered
    fixCalendarHeight();
    
    // Apply fix whenever window is resized
    window.addEventListener('resize', fixCalendarHeight);
    
    // Set interval to keep checking and fixing (helps with iOS Safari)
    const intervalId = setInterval(fixCalendarHeight, 1000);
    
    return () => {
      window.removeEventListener('resize', fixCalendarHeight);
      clearInterval(intervalId);
    };
  }, [isMobile]);
  
  // Add a function to load feedback for completed bookings
  const loadFeedbackForBookings = useCallback(async (bookings: CalendarBooking[]) => {
    if (!selectedBusiness?.id) return;
    
    const completedBookings = bookings.filter(booking => booking.extendedProps.status === 'completed');
    if (completedBookings.length === 0) return;
    
    const feedbackMap = new Map<string, { rating: number; hasText: boolean }>();
    
    // Load feedback for each completed booking
    await Promise.all(
      completedBookings.map(async (booking) => {
        try {
          const feedback = await getBookingFeedback(booking.id);
          if (feedback) {
            // Calculate average rating from form_responses or legacy fields
            let averageRating = 0;
            let ratingCount = 0;
            
            if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
              // Use dynamic form responses
              Object.values(feedback.form_responses).forEach(value => {
                if (typeof value === 'number' && value > 0) {
                  averageRating += value;
                  ratingCount++;
                }
              });
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            } else {
              // Fallback to legacy fields
              if (feedback.overall_quality_rating) {
                averageRating += feedback.overall_quality_rating;
                ratingCount++;
              }
              if (feedback.overall_experience_rating) {
                averageRating += feedback.overall_experience_rating;
                ratingCount++;
              }
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            }
            
            if (averageRating > 0) {
              feedbackMap.set(booking.id, {
                rating: averageRating,
                hasText: !!(feedback.improvement_suggestions || 
                          (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
              });
            }
          }
        } catch (error) {
          console.error('Error loading feedback for booking:', booking.id, error);
        }
      })
    );
    
    setBookingFeedback(feedbackMap);
  }, [selectedBusiness?.id]);
  
  // Calculate if we should start with today's date centered
  const getInitialDate = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Always start with today's date so calendar centers on current week
    return today;
  }, []);

  // Position calendar to today's date immediately after render, before showing
  useEffect(() => {
    if (calendarRef.current && bookings.length > 0 && !hasInitialScrolled.current && !calendarLoaded) {
      // Delay briefly to let FullCalendar fully render its DOM
      setTimeout(() => {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const todayCell = document.querySelector(`[data-date="${todayString}"]`) as HTMLElement;
        
        if (todayCell) {
          // Position to today's date instantly
          todayCell.scrollIntoView({
            behavior: 'auto', // No animation
            block: 'center',
            inline: 'center'
          });
          hasInitialScrolled.current = true;
        }
        
        // Always show the calendar after positioning attempt
        setCalendarLoaded(true);
      }, 100);
      
      // Fallback timeout - show calendar even if positioning fails
      setTimeout(() => {
        if (!calendarLoaded) {
          setCalendarLoaded(true);
        }
      }, 500);
    }
  }, [calendarRef.current, bookings.length, calendarLoaded]);
  
  // Reset calendar loaded state when month changes (for navigation)
  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const calendarMonth = currentDate.getMonth();
    const calendarYear = currentDate.getFullYear();
    
    // If we're not on today's month, show calendar immediately
    if (currentMonth !== calendarMonth || currentYear !== calendarYear) {
      setCalendarLoaded(true);
    } else if (!hasInitialScrolled.current) {
      // If we're back to today's month and haven't scrolled yet, hide calendar for positioning
      setCalendarLoaded(false);
    }
  }, [currentDate]);

  return (
    <div className="calendar-v2-container flex flex-col p-0 sm:p-4 pb-8 sm:pb-12 overflow-auto" 
         style={{ 
           minHeight: isMobile ? 'auto' : '100vh',
           scrollBehavior: 'smooth', // Add smooth scrolling behavior
           WebkitOverflowScrolling: 'touch' // Improve scrolling on iOS
         }}
    >
      <style jsx global>{`
        /* Calendar spacing */
        .calendar-v2-container .fc {
          margin-top: 8px;
        }
        
        /* Override any sticky behavior from FullCalendar */
        .fc-header-toolbar,
        .fc .fc-toolbar,
        .fc-header-toolbar.fc-toolbar,
        .fc-sticky,
        .fc-sticky-header {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Ensure the weekday headers aren't sticky */
        .fc-col-header,
        .fc-col-header-cell,
        .fc thead,
        .fc-scroller-harness,
        .fc .fc-scrollgrid-section,
        .fc .fc-scrollgrid-section-header,
        .fc .fc-scrollgrid-section-header > *,
        .fc .fc-scrollgrid-section-sticky {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Also fix any fixed table elements */
        table.fc-scrollgrid.fc-scrollgrid-liquid,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead tr,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead th {
          position: static !important;
          top: auto !important;
        }
        
        /* Mobile optimizations for space */
        @media (max-width: 640px) {
          .fc .fc-toolbar.fc-header-toolbar {
            margin-bottom: 0.5em;
          }
          
          .fc .fc-toolbar-title {
            font-size: 1.2em;
          }
          
          /* Reduce day header height */
          .fc-theme-standard th {
            padding: 4px 0 !important;
          }
          
          /* Smaller day numbers */
          .fc .fc-daygrid-day-top {
            padding: 2px 4px !important;
          }
          
          /* Optimize booking blocks for mobile */
          .fc-daygrid-event {
            padding: 1px 2px !important;
            margin-bottom: 1px !important;
          }
          
          /* Reduce space between booking blocks */
          .fc-daygrid-day-events {
            padding: 0 1px !important;
            margin: 0 !important;
          }
          
          /* Ensure day cells can expand as needed to fit all events */
          .fc .fc-daygrid-day-frame {
            min-height: auto !important;
            height: auto !important;
            padding: 2px 1px !important;
          }
          
          /* Remove any "more" link styling as it should no longer appear */
          .fc-daygrid-more-link {
            display: none !important;
          }
          
          /* Ensure day bottom padding doesn't cause any cut off */
          .fc-daygrid-day-bottom {
            padding: 0 !important;
          }
          
          /* Specific optimizations for iOS Safari */
          @supports (-webkit-touch-callout: none) {
            .fc .fc-daygrid-day-frame {
              padding-top: 2px !important;
            }
          }
          
          /* Make calendar stretch to edges on mobile */
          .fc {
            width: 100% !important; /* Use 100% instead of viewport width */
            max-width: 100% !important; /* Use 100% instead of viewport width */
            margin-left: 0 !important; /* Remove negative margin */
            margin-right: 0 !important;
            padding-left: 0 !important; /* Ensure no extra padding */
            padding-right: 0 !important; /* Remove right padding that was compensating for negative margin */
          }
          
          /* Remove excessive padding in calendar cells */
          .fc td, .fc th {
            padding: 0 !important;
          }
          
          /* Additional right edge fix */
          .fc td:last-child, 
          .fc th:last-child {
            border-right: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Fix table width to prevent overflow */
          .fc table {
            width: 100% !important; /* Use 100% instead of calc */
          }
        }
      `}</style>
      
      {/* Header with navigation controls that scrolls normally with the page */}
      <div className="flex flex-row items-center justify-between px-2 sm:px-4 bg-white py-2 sm:py-3 mb-2 sm:mb-3 rounded-md border border-gray-100 shadow-sm">
        <div className="flex items-center space-x-1 sm:space-x-2">
          <button 
            onClick={handlePrev}
            className="p-1 sm:p-2 rounded-md hover:bg-gray-100 border border-gray-200 flex items-center justify-center"
            disabled={isLoading}
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          
          <div className="text-base sm:text-lg font-medium px-2 sm:px-3">
            {calendarTitle}
          </div>
          
          <button
            onClick={handleNext}
            className="p-1 sm:p-2 rounded-md hover:bg-gray-100 border border-gray-200 flex items-center justify-center"
            disabled={isLoading}
          >
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          
          {/* Sales summary moved to the left - right after navigation controls */}
          {canAccess.calendarSales && (
            <div className="ml-3 sm:ml-6 text-right h-6 flex items-center justify-end">
              <span className="text-sm sm:text-base font-medium text-black">{formatCurrency(totalSales)}</span>
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 text-red-600 p-2 sm:p-3 rounded-md mx-2 sm:mx-0 mb-2 sm:mb-4 text-sm">
          {error}
        </div>
      )}

      
      <div className="flex-grow bg-white p-0 m-0 flex flex-col relative w-full" 
            style={{ 
              background: 'transparent', 
              boxShadow: 'none', 
              border: 'none',
              minHeight: 'auto',
              height: 'auto',
              position: 'static', // Prevent positioning issues on iOS
              overflowY: 'visible' // Make sure content can expand vertically
            }}>
        <style jsx global>{`
          /* Additional fix for left edge alignment */
          @media (max-width: 640px) {
            /* Target the calendar container to ensure it extends to the left edge */
            .fc-view-harness, 
            .fc-view-harness-active,
            .fc-daygrid,
            .fc-daygrid-body,
            .fc-scrollgrid,
            .fc-scrollgrid-liquid {
              padding-left: 0 !important;
              margin-left: 0 !important;
              left: 0 !important;
          }
            
            /* Target the calendar table to ensure it starts from the left edge */
            .fc table,
            .fc-scrollgrid-sync-table {
              margin-left: 0 !important;
              padding-left: 0 !important;
              left: 0 !important;
              width: 100% !important;
          }
            
            /* Adjust cell borders to ensure proper edge alignment */
            .fc td:first-child, 
            .fc th:first-child {
              border-left: 0 !important;
            }
            
            /* Fix any potential wrapper div issues */
            .fc-view {
              padding-left: 0 !important;
              margin-left: 0 !important;
          }
          }
          
          /* iOS-specific optimizations */
          /* Improve touch responsiveness */
          * {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            touch-action: manipulation;
          }
          
          /* Fix for iOS hover states - prevent stuck hover effects */
          @media (hover: none) {
            .fc-event:hover {
              transform: none !important;
              transition: none !important;
          }
            
            .fc a:hover {
              opacity: 1 !important;
          }
          }
          
          /* Improve scroll performance on iOS */
          .fc {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            -webkit-perspective: 1000;
            backface-visibility: hidden;
          }
          
          /* FIX SCROLLING OVER BOOKING BLOCKS */
          /* Allow scroll when touching booking blocks */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            touch-action: pan-y !important; /* Allow vertical scrolling on touch */
            pointer-events: auto !important; /* Still capture clicks but let scrolls through */
            /* Ensure events are tappable but don't block scrolling */
            position: relative;
            z-index: 5;
          }
          
          /* Fix for iOS scroll handling */
          @media (max-width: 768px) {
            /* Make all event blocks have pan-y behavior */
            .fc-daygrid-event-harness {
              touch-action: pan-y !important;
          }
            
            /* Inner parts of the event blocks should let touches pass through for scrolling */
            .fc-event-main,
            .fc-event-title,
            .fc-event-time {
              touch-action: pan-y !important;
          }
            
            /* Ensure container scroll works */
            .fc-view,
            .fc-scroller,
            .fc-scroller-liquid,
            .fc-scrollgrid {
              touch-action: pan-y !important;
          }
          }
          
          /* Prevent text selection on touch */
          .fc-daygrid-day-number,
          .fc-daygrid-day-top,
          .fc-event-title,
          .fc-event-time {
            -webkit-user-select: none;
            user-select: none;
          }
          
          /* Make tappable areas larger for mobile */
          @media (max-width: 768px) {
            .fc-event {
              min-height: 30px;
              padding: 4px;
          }
            
            .fc-daygrid-day-number {
              padding: 8px !important;
          }
            
            /* Increase tap target size for day cells */
            .fc-daygrid-day-frame {
              min-height: 80px !important;
          }
          }
          
          /* AGGRESSIVELY TARGET AND HIDE BACKGROUND ELEMENT */
          /* First, make all parent containers transparent */
          .fc-view-harness,
          .fc-view-harness-active,
          .fc-theme-standard .fc-view-harness {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          
          /* Remove all background containers that might have styling */
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-liquid,
          .fc .fc-scrollgrid-section,
          .fc-scrollgrid-sync-container,
          .fc-scrollgrid-sync-table,
          .fc .fc-scrollgrid-section-liquid {
            background-color: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            opacity: 1 !important;
          }
          
          /* Main calendar container */
          .fc {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          /* This is the border container for the entire table */
          .fc .fc-scrollgrid {
            border: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }
          
          /* Create cell borders directly */
          .fc-theme-standard td, 
          .fc-theme-standard th {
            border: 1px solid #e5e7eb !important;
            background-color: white !important;
          }
          
          /* Ensure frame expansion for all cells */
          .fc .fc-daygrid-day-frame {
            height: auto !important;
            min-height: 120px !important;
            background-color: white !important;
          }
          
          /* Force table wrapping container to be transparent */
          .fc-view, 
          .fc-dayGridMonth-view,
          .fc-view-harness, 
          .fc-view-harness-active,
          table.fc-scrollgrid {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Reset scrolling container styles */
          .fc, 
          .fc .fc-view,
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-section,
          .fc .fc-scrollgrid-section-body,
          .fc .fc-scrollgrid-section-header,
          .fc .fc-scroller,
          .fc .fc-scroller-harness,
          .fc .fc-scroller-liquid,
          .fc .fc-scroller-liquid-absolute,
          .fc .fc-daygrid,
          .fc .fc-daygrid-body,
          .fc .fc-scrollgrid-sync-table {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }
          
          /* DO NOT TOUCH BOOKING BLOCKS */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            /* Preserve these untouched */
          }
          
          /* Preserve cell styling for day frames */
          .fc-daygrid-day-frame {
            background-color: white !important;
          }
          
          /* Other necessary styles */
          .fc-daygrid-day,
          .fc-daygrid-day-top {
            background-color: white !important;
          }
          
          /* Last container attempt - target absolutely everything */
          div[class*="fc-"] {
            border-radius: 0 !important;
          }
          
          /* Calendar today styling */
          .fc .fc-daygrid-day.fc-day-today {
            background: transparent !important;
          }
          
          .calendar-today-pill {
            background: #3b82f6 !important;
            color: #fff !important;
            border-radius: 9999px;
            padding: 2px 8px;
            font-weight: 600;
            display: inline-block;
          }
          
          /* Fix the scrolling container styles to allow full display */
          .fc .fc-view-harness,
          .fc-view-harness-active,
          .fc-daygrid,
          .fc-daygrid-body,
          .fc-scrollgrid,
          .fc-scrollgrid-liquid,
          .fc-scroller,
          .fc-scroller-harness {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          
          /* Ensure the entire grid can expand */
          .fc-scrollgrid-sync-table {
            height: auto !important;
          }
          
          /* Make each cell able to grow as needed */
          .fc .fc-daygrid-body-balanced .fc-daygrid-day-events {
            position: relative !important;
          }
          
          /* Ensure all events show and are visible */
          .fc-v-event, .fc-h-event, .fc-daygrid-event {
            visibility: visible !important;
            display: block !important;
          }
        `}</style>
        <div 
          className="w-full md:px-0 overflow-visible relative" 
          style={{ 
            background: 'transparent', 
            touchAction: 'manipulation',
            WebkitOverflowScrolling: 'touch',
            marginLeft: 0,
            paddingLeft: 0,
            opacity: calendarLoaded ? 1 : 0,
            transition: calendarLoaded ? 'opacity 0.1s ease-in' : 'none'
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            initialDate={getInitialDate()}
            headerToolbar={false}
            height="auto"
            contentHeight="auto"
            aspectRatio={isMobile ? 0.8 : 1.7}
            dayMaxEvents={false}
            firstDay={1}
            weekNumbers={false}
            navLinks={false}
            editable={false}
            selectable={false} // Disable selection to improve scrolling
            longPressDelay={100}
            selectLongPressDelay={300}
            dayHeaderFormat={{ weekday: 'short' }}
            stickyHeaderDates={false}
            expandRows={!isMobile}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            themeSystem="standard"
            slotEventOverlap={false}
            fixedWeekCount={false}
            businessHours={false}
            nowIndicator={true}
            datesSet={handleDatesSet}
            events={calendarEvents}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            dayCellContent={renderDayCellContent}
            lazyFetching={false}
            forceEventDuration={true}
            handleWindowResize={true}
            rerenderDelay={0}
            eventDisplay="block"
            eventMinHeight={isMobile ? 18 : 25}
            eventMinWidth={10}
            eventShortHeight={isMobile ? 18 : 25}
            eventLongPressDelay={400} // Increase to help distinguish between tap and scroll
            eventOverlap={false}
            eventOrder="extendedProps.booking_number"
            eventConstraint={{
              startTime: '00:00',
              endTime: '24:00',
              dows: [0, 1, 2, 3, 4, 5, 6]
            }}
            dateClick={(info) => {
              // When a date is clicked, scroll it into view
              const cell = info.dayEl;
              if (cell) {
                cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
          />
        </div>
      </div>
      
      {isBookingInfoModalOpen && selectedBooking && (
        <BookingInfoModal
          isOpen={isBookingInfoModalOpen}
          onClose={() => {
            console.log('📱 User manually closed modal, clearing localStorage');
            clearModalState(); // Clear localStorage immediately
            setSelectedBooking(null);
            setIsBookingInfoModalOpen(false);
          }}
          booking={selectedBooking}
          onUpdate={() => fetchBookings(true)}
        />
      )}
      <DaySummaryModal
        isOpen={isDaySummaryOpen}
        onClose={() => setIsDaySummaryOpen(false)}
        selectedDate={selectedDay}
        position={daySummaryPosition || { x: 0, y: 0, width: 0, height: 0 }}
        isLocationBooking={true}
        onCreateBooking={() => {}}
        onBookingClick={() => {}}
      />
    </div>
  );
} 